import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer } from "@angular/core";
import { NavigationEnd, RouteReuseStrategy, Router, provideRouter, withNavigationErrorHandler } from "@angular/router";
import { AuthClientConfig, provideAuth0 } from "@auth0/auth0-angular";
import { authInterceptor } from "@kno2/core/auth";
import { WINDOW_TOKEN } from "@kno2/core/browser";
import { APP_CONFIG_TOKEN, AppConfig, AppConfigService } from "@kno2/core/config";
import { apiInterceptor, cacheInterceptor, errorMessageInterceptor, tokenInterceptor } from "@kno2/core/http";
import { CustomRouteReuseStrategy } from "@kno2/core/routing";
import { provideLocalStorage, provideSessionStorage, SessionStorageService } from "@kno2/core/storage";
import { ApplicationInsightsService, TelemetryErrorHandler } from "@kno2/core/telemetry";
import { DIALOG_SERVICE_TOKEN, provideKno2PrimeNG } from "@kno2/core/theme";
import { DIRECTORY_API_BASE_URL } from "@kno2/data-access/directory-api";
import { UnsupportedBrowserService } from "@kno2/features/error";
import { providePdfViewer } from "@kno2/ngx-pdf-viewer";
import { loadingInterceptor } from "@kno2/shared/loading-indicator";
import { secondsToMilliseconds } from "date-fns";
import { provideCharts, withDefaultRegisterables } from "ng2-charts";
import { MessageService } from "primeng/api";
import { DialogService as PDialogService } from "primeng/dynamicdialog";
import { filter } from "rxjs";
import { routes } from "./app.routes";
export const appConfig: ApplicationConfig = {
    providers: [
        provideAuth0(),
        provideRouter(
            routes,
            withNavigationErrorHandler((event) => {
                const message = event.error instanceof Error ? event.error.message : String(event.error);
                const isStaleChunk = message.includes("Failed to fetch dynamically imported module") || message.includes("Importing a module script failed");
                if (!isStaleChunk) return;

                const sessionStorage = inject(SessionStorageService);
                const windowRef = inject(WINDOW_TOKEN);
                const lastReload = sessionStorage.get<{ url: string; at: number }>("chunkReload");
                if (lastReload?.url === event.url && Date.now() - lastReload.at < secondsToMilliseconds(30)) return;
                sessionStorage.set("chunkReload", { url: event.url, at: Date.now() });
                windowRef.location.assign(event.url);
            })
        ),
        { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
        provideHttpClient(withInterceptors([loadingInterceptor, cacheInterceptor, tokenInterceptor, apiInterceptor, authInterceptor, errorMessageInterceptor])),
        provideLocalStorage({ keyPrefix: "kno2" }),
        provideSessionStorage({ keyPrefix: "kno2" }),
        providePdfViewer(),
        MessageService,
        provideCharts(withDefaultRegisterables()),
        provideKno2PrimeNG(),
        {
            provide: DIALOG_SERVICE_TOKEN,
            useClass: PDialogService
        },
        {
            provide: APP_CONFIG_TOKEN,
            useFactory: (appConfigService: AppConfigService) => appConfigService.appConfig,
            deps: [AppConfigService]
        },
        {
            provide: DIRECTORY_API_BASE_URL,
            useFactory: (appConfig: AppConfig) => `${appConfig.baseApiUrl}/api/query/organizations`,
            deps: [APP_CONFIG_TOKEN]
        },
        {
            provide: ErrorHandler,
            useClass: TelemetryErrorHandler
        },
        provideAppInitializer(() => {
            const initializerFn = ((unsupportedBrowserService: UnsupportedBrowserService) => (): void => {
                unsupportedBrowserService.checkBrowserSupport();
            })(inject(UnsupportedBrowserService));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = (
                (appConfigService: AppConfigService, authClientConfig: AuthClientConfig, appInsightsService: ApplicationInsightsService) => async () => {
                    const appConfig = await appConfigService.load();
                    authClientConfig.set({
                        domain: appConfig.auth0.domain,
                        clientId: appConfig.auth0.clientId,
                        errorPath: "/auth-error",
                        authorizationParams: {
                            redirect_uri: appConfig.auth0.redirectUri,
                            audience: appConfig.auth0.audience,
                            responseType: "token id_token",
                            scope: "openid offline_access"
                        },
                        skipRedirectCallback: window.location.pathname === "/oauth/callback/idproofing" || window.location.pathname === "/oauth/callback/pccsso"
                    });

                    appInsightsService.load(appConfig.appInsightInstrumentationKey);
                }
            )(inject(AppConfigService), inject(AuthClientConfig), inject(ApplicationInsightsService));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = ((router: Router, windowRef: Window) => (): void => {
                router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
                    const intercom = windowRef.Intercom;
                    if (intercom) {
                        intercom("update");
                    }
                });
            })(inject(Router), inject(WINDOW_TOKEN));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            return new Promise<void>((resolve) => {
                const checkJQuery = () => {
                    if (typeof window["jQuery"] === "function") {
                        resolve();
                    } else {
                        setTimeout(checkJQuery, 10);
                    }
                };
                checkJQuery();
            });
        })
    ]
};
